import { useState, type FormEvent } from 'react'
import { Lock, Loader2, AlertCircle } from 'lucide-react'
import { ShotSelectLogo } from './ShotSelectLogo'

/**
 * Gallery passcode gate. A studio can protect a gallery with a passcode (the
 * link's download_pin doubles as the gallery passcode). The worker now 401s the
 * SPA's JSON manifest fetch with `{error:'passcode_required'}` when a pin is set
 * and none/wrong is supplied — so the SPA can no longer render a gated gallery
 * un-gated. This screen collects the passcode and threads it back through
 * `onSubmit`, which re-loads the manifest with `?pin=`. On success the pin is
 * persisted (api/client) so reloads and downloads stay authorized.
 *
 * `wrongPin` flips the inline error after a rejected attempt without leaving the
 * passcode state. `submitting` drives the spinner while the re-load is in flight.
 */
export function PasscodeScreen({
  onSubmit,
  wrongPin,
  submitting,
}: {
  onSubmit: (pin: string) => void
  wrongPin: boolean
  submitting: boolean
}) {
  const [pin, setPin] = useState('')
  const canSubmit = pin.trim().length > 0 && !submitting

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    if (!canSubmit) return
    onSubmit(pin.trim())
  }

  return (
    <div
      data-testid="passcode-screen"
      className="flex min-h-[100dvh] flex-col items-center justify-center gap-5 px-6 text-center"
    >
      <div className="grid h-14 w-14 place-items-center rounded-full border border-[var(--border-default)] bg-[var(--surface-secondary)] text-[var(--text-secondary)]">
        <Lock size={24} strokeWidth={2} aria-hidden />
      </div>
      <div className="max-w-xs">
        <div className="mb-1 flex items-center justify-center gap-2 text-[var(--text-primary)]">
          <ShotSelectLogo className="h-4 w-4" />
          <span className="text-base font-semibold">Protected gallery</span>
        </div>
        <p className="text-[13px] text-[var(--text-secondary)]">
          Your photographer set a passcode for this gallery. Enter it to continue.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="flex w-full max-w-xs flex-col gap-3">
        <label htmlFor="gallery-passcode" className="sr-only">
          Gallery passcode
        </label>
        <input
          id="gallery-passcode"
          data-testid="passcode-input"
          type="password"
          inputMode="numeric"
          autoComplete="off"
          autoFocus
          value={pin}
          onChange={(e) => setPin(e.target.value)}
          aria-invalid={wrongPin}
          placeholder="Passcode"
          className="h-12 w-full rounded-xl border border-[var(--border-default)] bg-[var(--surface-secondary)] px-4 text-center text-base tracking-[0.3em] text-[var(--text-primary)] outline-none focus:border-[var(--text-tertiary)]"
        />
        {wrongPin && (
          <div
            role="alert"
            data-testid="passcode-error"
            className="flex items-center justify-center gap-1.5 text-[12px] text-[var(--accent-danger,#ef4444)]"
          >
            <AlertCircle size={13} strokeWidth={2} aria-hidden />
            That passcode didn't match. Try again.
          </div>
        )}
        <button
          type="submit"
          data-testid="passcode-submit"
          disabled={!canSubmit}
          className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-[var(--accent-primary)] text-sm font-semibold text-[#0a0a0a] disabled:cursor-not-allowed disabled:opacity-40"
        >
          {submitting ? (
            <>
              <Loader2 size={15} className="animate-spin" aria-hidden /> Unlocking…
            </>
          ) : (
            'Unlock gallery'
          )}
        </button>
      </form>
    </div>
  )
}
